import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  PLATFORM_ID,
  viewChild,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { fromEvent } from 'rxjs';

import { ButtonComponent } from '../button/button.component';
import { PpCoinComponent } from '../pp-coin/pp-coin.component';
import {
  blockedDialogGuestText,
  blockedDialogInsufficientText,
  PERRON_POINTS_COPY,
} from '../../perron-points/perron-points-copy';
import { evaluateEarlyAccess } from '../../perron-points/early-access-gate';
import { AuthModalService } from '../../services/auth-modal.service';
import { AuthService } from '../../services/auth.service';
import { EarlyAccessBlockedDialogService } from '../../services/early-access-blocked-dialog.service';
import { PerronPointsModalService } from '../../services/perron-points-modal.service';
import { PointsService } from '../../services/points.service';

/** Variantes de R2, en el orden en el que las resuelve `evaluateEarlyAccess`. */
type BlockedVariant = 'guest' | 'insufficient' | 'loading' | 'error';

/**
 * Diálogo de bloqueo de «Agregar al carrito» para productos de Acceso VIP
 * (R2). El producto NO entra al carrito: `CartService.addLine` corta antes de
 * llamar al API y abre esto.
 *
 * Hermano de `coverage-notice-dialog` (mismo patrón de signals, focus-trap y
 * scroll-lock que restaura el valor previo del body), con dos diferencias:
 * tiene DOS CTAs —el de acción cambia por variante y el secundario siempre
 * cierra— y SÍ se cierra con Escape o con el backdrop, porque «Seguir viendo»
 * es una salida legítima: aquí no hay nada que aceptar.
 *
 * Se monta una única vez en `app.ts` y se gobierna por
 * `EarlyAccessBlockedDialogService`.
 */
@Component({
  selector: 'app-early-access-blocked-dialog',
  imports: [ButtonComponent, PpCoinComponent],
  templateUrl: './early-access-blocked-dialog.component.html',
  styleUrl: './early-access-blocked-dialog.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EarlyAccessBlockedDialogComponent {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialogService = inject(EarlyAccessBlockedDialogService);
  private readonly authService = inject(AuthService);
  private readonly authModalService = inject(AuthModalService);
  private readonly pointsService = inject(PointsService);
  private readonly perronPointsModalService = inject(PerronPointsModalService);

  protected readonly isOpen = this.dialogService.isOpen;
  protected readonly context = this.dialogService.context;
  protected readonly copy = PERRON_POINTS_COPY.blockedDialog;

  /**
   * Variante viva: se recalcula mientras el diálogo está abierto, de modo que
   * el «Estamos verificando…» se convierte solo en el mensaje definitivo
   * cuando `/perron-points/me` resuelve. Si al resolver el cliente SÍ cumple,
   * el efecto de más abajo cierra el diálogo (nunca lo dejamos afirmando un
   * bloqueo que ya no existe); el cliente vuelve a pulsar «Agregar».
   */
  private readonly decision = computed(() =>
    evaluateEarlyAccess(this.context()?.threshold ?? null, {
      authenticated: this.authService.isAuthenticated(),
      balance: this.pointsService.balance(),
      // `threshold()` no nulo ⟺ `/perron-points/me` ya resolvió.
      balanceKnown: this.pointsService.threshold() !== null,
      balanceError: this.pointsService.loadError(),
    }),
  );

  protected readonly variant = computed<BlockedVariant>(() => {
    const decision = this.decision();
    if (!decision.allowed) return decision.reason;
    // Estado transitorio: el bloqueo dejó de aplicar y el efecto de abajo va a
    // cerrar el diálogo. Mientras tanto, «verificando» — nunca un motivo que
    // ya es falso, por si el template llegara a repintarse antes del cierre.
    return 'loading';
  });

  protected readonly body = computed(() => {
    const threshold = this.context()?.threshold ?? 0;
    switch (this.variant()) {
      case 'loading':
        return this.copy.loading;
      case 'error':
        return this.copy.error;
      case 'insufficient':
        return blockedDialogInsufficientText(threshold, this.pointsService.balance());
      default:
        return blockedDialogGuestText(threshold);
    }
  });

  /** CTA de acción. `null` mientras se verifica: no hay nada que pulsar todavía. */
  protected readonly actionLabel = computed<string | null>(() => {
    switch (this.variant()) {
      case 'loading':
        return null;
      case 'error':
        return this.copy.errorCta;
      case 'insufficient':
        return this.copy.insufficientCta;
      default:
        return this.copy.guestCta;
    }
  });

  /** La moneda solo acompaña a los mensajes que hablan de saldo. */
  protected readonly showCoin = computed(
    () => this.variant() === 'guest' || this.variant() === 'insufficient',
  );

  private readonly dialogRef = viewChild<ElementRef<HTMLElement>>('dialog');
  private previouslyFocusedElement: HTMLElement | null = null;
  private previousBodyOverflow = '';

  constructor() {
    // El bloqueo dejó de aplicar mientras el diálogo estaba abierto (la
    // verificación resolvió a favor del cliente, o inició sesión desde el CTA):
    // cerramos en vez de mentir.
    effect(() => {
      if (this.isOpen() && this.decision().allowed) this.dialogService.close();
    });

    // Foco + scroll-lock del body RESTAURANDO el valor previo: el diálogo puede
    // abrirse sobre el drawer del carrito o sobre el modal de zoom, que ya lo
    // tenían bloqueado; forzarlo a '' al cerrar reactivaría el scroll de fondo.
    effect(() => {
      const isOpen = this.isOpen();
      if (!isPlatformBrowser(this.platformId)) return;

      if (isOpen) {
        this.previousBodyOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        this.previouslyFocusedElement = (document.activeElement as HTMLElement | null) ?? null;
        queueMicrotask(() => {
          const root = this.dialogRef()?.nativeElement;
          if (root) this.getFocusableElements(root)[0]?.focus();
        });
      } else {
        document.body.style.overflow = this.previousBodyOverflow;
        if (this.previouslyFocusedElement) {
          const el = this.previouslyFocusedElement;
          this.previouslyFocusedElement = null;
          queueMicrotask(() => {
            if (document.contains(el)) el.focus({ preventScroll: true });
          });
        }
      }
    });

    // Escape cierra (equivale a «Seguir viendo») y Tab/Shift+Tab ciclan dentro.
    afterNextRender(() => {
      fromEvent<KeyboardEvent>(document, 'keydown')
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((e) => {
          if (!this.isOpen()) return;

          if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            this.onDismiss();
            return;
          }

          if (e.key !== 'Tab') return;

          const modal = this.dialogRef()?.nativeElement;
          if (!modal) return;

          const focusables = this.getFocusableElements(modal);
          if (focusables.length === 0) {
            e.preventDefault();
            return;
          }

          const first = focusables[0];
          const last = focusables[focusables.length - 1];
          const active = document.activeElement as HTMLElement | null;

          if (!active || !modal.contains(active)) {
            e.preventDefault();
            first.focus();
            return;
          }

          if (e.shiftKey && active === first) {
            e.preventDefault();
            last.focus();
          } else if (!e.shiftKey && active === last) {
            e.preventDefault();
            first.focus();
          }
        });
    });
  }

  /** «Seguir viendo», Escape y backdrop: la misma salida. */
  protected onDismiss(): void {
    this.dialogService.close();
  }

  protected onAction(): void {
    switch (this.variant()) {
      case 'loading':
        return;
      case 'error':
        this.pointsService.refresh();
        return;
      case 'insufficient':
        this.dialogService.close();
        this.perronPointsModalService.open();
        return;
      default:
        // Cerramos antes de abrir el modal de login: este diálogo vive por
        // encima del zoom de producto (z-index 1000), y el modal de auth
        // comparte esa capa, así que dejarlos apilados lo enterraría.
        this.dialogService.close();
        this.authModalService.open('login');
    }
  }

  /** Devuelve los elementos enfocables dentro del modal en orden DOM. */
  private getFocusableElements(root: HTMLElement): HTMLElement[] {
    const selector = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(',');
    return Array.from(root.querySelectorAll<HTMLElement>(selector)).filter(
      (el) => el.offsetParent !== null || el === document.activeElement,
    );
  }
}
